import { AuthLayout } from '@/bundle/Auth/ui/AuthLayout/AuthLayout';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { AuthStepWrapper } from '@/bundle/Auth/ui/AuthStep/styles';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { Button } from '@/components/Button/Button';
import {
  DashedLine,
  OverageRateBlock,
} from '@/bundle/Auth/AdminOnboardingFlow/SelectBillingPlanPage/ui/PricingPlanCard/styles';
import { useState } from 'react';
import { OverageWiresCounter } from '@/bundle/_MasterAdmin/_BuyOverageWiresPage/ui/OverageWiresCounter';
import { createSearchParams, useNavigate } from 'react-router';
import { getMasterAdminBillingPath } from '@/bundle/_MasterAdmin/_BillingPage/path/path';
import { AuthHeading } from '@/bundle/Auth/ui/AuthHeading/AuthHeading';
import { getSinglePaymentCheckoutPath } from '@/bundle/_MasterAdmin/_SinglePaymentCheckoutPage/path/path';
import { CheckoutCardInfoRow } from '@/bundle/_MasterAdmin/_BillingPage/ui/CheckoutCardInfoRow';
import { MAX_OVERAGE_WIRE_COUNT, MIN_OVERAGE_WIRE_COUNT } from './const/const';
import { useQuery } from '@tanstack/react-query';
import { convertCentsToDollars } from '@/helpers/formatHelpers';
import { getBillingPlanDetails } from '@/bundle/shared/api';
import { LayoutSpinner } from '@/bundle/Layouts/styles';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { Notification } from '@/components/Notification/Notification';
import { NavigationLink } from '@/components/NavigationLink/NavigationLink';
import { useUser } from '@/context/userContext';
import { getBillingDetails } from '@/helpers/billingHelpers';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';

export const BuyOverageWiresPage = () => {
  const navigate = useNavigate();
  const { navigateBack } = useStatefulNavigation();
  const { user } = useUser();
  const [overageWiresQuantity, setOverageWiresQuantity] = useState(MIN_OVERAGE_WIRE_COUNT);
  const planId = getBillingDetails(user)?.current_plan?.id;

  const { data, isPending } = useQuery({
    queryKey: ['get_billing_plan_details', planId],
    queryFn: () => getBillingPlanDetails(planId),
    enabled: !!planId,
  });

  const incrementOverageQuantity = () => {
    if (overageWiresQuantity === MAX_OVERAGE_WIRE_COUNT) return;

    setOverageWiresQuantity((quantity) => quantity + MIN_OVERAGE_WIRE_COUNT);
  };

  const decrementOverageQuantity = () => {
    if (overageWiresQuantity === MIN_OVERAGE_WIRE_COUNT) return;

    setOverageWiresQuantity((quantity) => quantity - MIN_OVERAGE_WIRE_COUNT);
  };

  const navigateToSinglePaymentCheckoutPage = () => {
    navigate({
      pathname: getSinglePaymentCheckoutPath(),
      search: createSearchParams({
        wires_quantity: String(overageWiresQuantity),
      }).toString(),
    });
  };

  const planDetails = data?.body;
  const overageWirePrice = convertCentsToDollars(planDetails?.extra_view_price_amount);
  const totalOverageWiresPrice = planDetails?.extra_view_price_amount * overageWiresQuantity;
  const convertedTotalOverageWiresPrice = convertCentsToDollars(totalOverageWiresPrice);
  const error = getResponseError(data?.error);

  return (
    <AuthLayout position='top'>
      <AuthHeading
        header='Buy Overage Wires'
        subHeader={
          <>
            Choose the number of overage wires you would like to purchase. <br />
            The price per wire depends on your current plan.
          </>
        }
      >
        <Typography variant='bodySmall' color={COLORS.grey[650]}>
          You can buy up to {MAX_OVERAGE_WIRE_COUNT} overage wires at a time.
        </Typography>
      </AuthHeading>

      {error && (
        <>
          <Notification variant='error' marginBottom='16px'>
            {error}
          </Notification>
          <NavigationLink to={getMasterAdminBillingPath()}>{'< Return to Billing'}</NavigationLink>
        </>
      )}
      {isPending && <LayoutSpinner />}

      {planDetails && (
        <AuthStepWrapper>
          <Box display='flex' flexDirection='column' marginBottom='24px'>
            <Typography color={COLORS.black} fontWeight='semibold' marginBottom='12px'>
              Wires Number
            </Typography>
            <Box marginBottom='24px'>
              <OverageWiresCounter
                onIncrement={incrementOverageQuantity}
                onDecrement={decrementOverageQuantity}
                count={overageWiresQuantity}
              />
            </Box>

            <OverageRateBlock>
              <Box display='flex' flexDirection='column' rowGap='4px'>
                <CheckoutCardInfoRow title='Current Plan' value={`${planDetails?.name} Plan`} />
                <CheckoutCardInfoRow title='Price per Wire' value={overageWirePrice} />
              </Box>
              <Box marginTop='12px' marginBottom='8px'>
                <DashedLine />
              </Box>
              <CheckoutCardInfoRow title='Total Price' value={convertedTotalOverageWiresPrice} variant='summary' />
            </OverageRateBlock>
          </Box>

          <ButtonGroup>
            <Button variant='secondary' onClick={() => navigateBack(getMasterAdminBillingPath())}>
              Cancel
            </Button>
            <Button onClick={navigateToSinglePaymentCheckoutPage}>Buy now</Button>
          </ButtonGroup>
        </AuthStepWrapper>
      )}
    </AuthLayout>
  );
};
